'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

interface DepositPaymentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  auctionTitle: string;
  depositAmount: number;
  auctionId: string;
  image: string;
  depositCode: string;
  districtName: string;
  cityName: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountHolder: string;
  iban: string;
  accountType: 'checking' | 'savings';
}

export default function DepositPaymentPopup({
  isOpen,
  onClose,
  auctionTitle,
  depositAmount,
  auctionId: _auctionId,
  image,
  depositCode,
  districtName,
  cityName
}: DepositPaymentPopupProps) {
  const t = useTranslations('depositPayment');
  const [copiedDescription, setCopiedDescription] = useState(false);
  const [copiedIbans, setCopiedIbans] = useState<{ [key: string]: boolean }>({});

  // Mock bank accounts data - sadece şirket hesapları
  const bankAccounts: BankAccount[] = [
    {
      id: '1',
      bankName: 'İş Bankası',
      accountHolder: 'Trink Emlak A.Ş.',
      iban: 'TR12 0006 4000 0001 2345 6789 01',
      accountType: 'checking'
    },
    {
      id: '2',
      bankName: 'Yapı Kredi',
      accountHolder: 'Trink Emlak A.Ş.',
      iban: 'TR67 0006 7000 0000 1234 5678 90',
      accountType: 'checking'
    },
    {
      id: '3',
      bankName: 'Ziraat Bankası',
      accountHolder: 'Trink Emlak A.Ş.',
      iban: 'TR33 0001 0002 3456 7890 1234 56',
      accountType: 'checking'
    }
  ];



  const copyToClipboard = (text: string, type: 'iban' | 'description', ibanId?: string) => {
    navigator.clipboard.writeText(text);
    
    if (type === 'description') {
      setCopiedDescription(true);
      // 2 saniye sonra icon'u geri değiştir
      setTimeout(() => {
        setCopiedDescription(false);
      }, 2000);
    } else if (type === 'iban' && ibanId) {
      setCopiedIbans(prev => ({ ...prev, [ibanId]: true }));
      // 2 saniye sonra icon'u geri değiştir
      setTimeout(() => {
        setCopiedIbans(prev => ({ ...prev, [ibanId]: false }));
      }, 2000);
    }
  };

  const generatePaymentDescription = (depositCode: string) => {
    return depositCode;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Popup */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto scrollbar-hide">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Property Image */}
        <div className="relative h-56 w-full">
          <Image 
            src={image} 
            alt={auctionTitle}
            fill
            className="object-cover rounded-t-2xl"
          />
          <div className="absolute inset-0 bg-black/20 rounded-t-2xl"></div>
        </div>

        {/* Header */}
        <div className="border-b border-gray-200">
          <div className="text-center">
            <p className="text-gray-600">{t('subtitle')}</p>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pt-6 pb-4">
          {/* Auction Info */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <div className="flex items-center mb-2">
              <svg className="w-5 h-5 text-gray-900 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <h3 className="font-semibold text-gray-900">{t('auctionInfo')}</h3>
            </div>
            <p className="text-gray-900 font-medium mb-1">{auctionTitle}</p>
            <p className="text-gray-700 text-sm">{districtName}, {cityName}</p>
          </div>

          {/* Payment Information */}
          <div className="space-y-4">
            {/* Payment Details */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h4 className="font-medium text-green-900">{t('paymentDetails')}</h4>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">{t('amount')}:</span>
                  <span className="font-semibold text-green-900">₺{depositAmount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-green-700">{t('description')}:</span>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-sm text-green-900">{generatePaymentDescription(depositCode)}</span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(generatePaymentDescription(depositCode), 'description')}
                      className="p-1 text-green-600 hover:text-green-800 transition-colors"
                      title={t('copyDescription')}
                    >
                      {copiedDescription ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Accounts Information */}
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                <h3 className="font-semibold text-gray-900">{t('bankAccounts')}</h3>
              </div>
              
              {/* Company Bank Accounts */}
              <div className="space-y-2">
                {bankAccounts.map((account) => (
                  <div key={account.id} className="flex items-center p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <div className="flex-1">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-gray-900">{account.bankName}</p>
                          <p className="text-sm text-gray-600">{account.accountHolder}</p>
                          <p className="text-sm text-gray-900 font-mono font-semibold">{account.iban}</p>
                        </div>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {t('companyAccount')}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(account.iban, 'iban', account.id)}
                      className="ml-2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={t('copyIban')}
                    >
                      {copiedIbans[account.id] ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex">
                <svg className="w-5 h-5 text-yellow-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">{t('importantNotice')}</h3>
                  <div className="mt-1 text-sm text-yellow-700">
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>{t('notice1')}</li>
                      <li>{t('notice2')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              onClick={onClose}
              className="w-full py-2 px-4 bg-gray-600 text-white rounded-lg font-semibold hover:bg-gray-700 transition-colors mt-3"
            >
              {t('close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
